import { useEffect, useMemo, useState } from 'react';
import api from '../api/axios';
import Layout from '../components/Layout';
import Toast from '../components/Toast';
import { SkeletonTable } from '../components/Skeleton';
import { formatCurrency as fmt } from '../utils/currency';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', sky: '#38BDF8', text: '#E8EDF5', sub: '#C5CDD8',
};

const STYLES = `
  .pu-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 28px 32px 20px; border-bottom: 1px solid ${C.border};
  }
  .pu-title-row { display: flex; align-items: center; gap: 10px; }
  .pu-title-row h2 { font-size: 1.35rem; font-weight: 800; color: ${C.text}; margin: 0; }
  .pu-count { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 999px;
    background: rgba(240,180,41,0.15); color: ${C.gold}; font-size: 0.72rem; font-weight: 700;
    border: 1px solid rgba(240,180,41,0.3); }
  .pu-subtitle { color: ${C.muted}; font-size: 0.82rem; margin-top: 4px; }
  .pu-btn-primary {
    display: inline-flex; align-items: center; gap: 6px; padding: 10px 18px;
    background: ${C.gold}; color: #0B0E17; border: none; border-radius: 10px;
    font-size: 0.82rem; font-weight: 700; cursor: pointer; transition: opacity .15s;
  }
  .pu-btn-primary:hover { opacity: .88; }

  .pu-body { padding: 24px 32px; display: flex; flex-direction: column; gap: 24px; }

  .pu-kpi-grid {
    display: grid; grid-template-columns: repeat(5, 1fr); gap: 14px;
  }
  @media (max-width: 1100px) { .pu-kpi-grid { grid-template-columns: repeat(3, 1fr); } }
  @media (max-width: 720px) { .pu-kpi-grid { grid-template-columns: 1fr 1fr; } }

  .pu-kpi {
    background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 14px;
    padding: 18px 20px; display: flex; flex-direction: column; gap: 6px;
  }
  .pu-kpi--featured {
    grid-column: span 1; border-color: rgba(0,201,177,0.3);
    background: linear-gradient(135deg, rgba(0,201,177,0.08) 0%, ${C.surface} 60%);
  }
  .pu-kpi-label { font-size: 0.72rem; font-weight: 600; color: ${C.muted}; text-transform: uppercase; letter-spacing: .04em; }
  .pu-kpi-value { font-size: 1.55rem; font-weight: 800; color: ${C.teal}; font-family: 'JetBrains Mono', monospace; line-height: 1.1; }
  .pu-kpi-sub { font-size: 0.75rem; color: ${C.sub}; }
  .pu-kpi-mini { display: flex; flex-direction: column; gap: 3px; margin-top: 4px; }
  .pu-kpi-mini span { font-size: 0.7rem; color: ${C.muted}; }
  .pu-kpi-icon {
    width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center;
    justify-content: center; font-size: 0.75rem; font-weight: 800; margin-bottom: 4px;
  }
  .pu-kpi-icon--teal { background: rgba(0,201,177,0.15); color: ${C.teal}; border: 1px solid rgba(0,201,177,0.3); }
  .pu-kpi-icon--sky { background: rgba(56,189,248,0.12); color: ${C.sky}; border: 1px solid rgba(56,189,248,0.25); }
  .pu-kpi-icon--coral { background: rgba(255,107,107,0.12); color: ${C.coral}; border: 1px solid rgba(255,107,107,0.25); }
  .pu-kpi-icon--gold { background: rgba(240,180,41,0.12); color: ${C.gold}; border: 1px solid rgba(240,180,41,0.25); }
  .pu-kpi-num { font-size: 1.4rem; font-weight: 800; color: ${C.text}; font-family: 'JetBrains Mono', monospace; }
  .pu-kpi-caption { font-size: 0.72rem; color: ${C.muted}; }

  .pu-board { display: grid; grid-template-columns: 1fr 320px; gap: 20px; }
  @media (max-width: 900px) { .pu-board { grid-template-columns: 1fr; } }

  .pu-section {
    background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 14px; overflow: hidden;
  }
  .pu-section-head {
    display: flex; justify-content: space-between; align-items: flex-start;
    padding: 18px 20px 14px; border-bottom: 1px solid ${C.border};
  }
  .pu-section-title { font-size: 0.9rem; font-weight: 700; color: ${C.text}; }
  .pu-section-sub { font-size: 0.75rem; color: ${C.muted}; margin-top: 3px; }
  .pu-section-meta { font-size: 0.72rem; color: ${C.muted}; white-space: nowrap; }

  .pu-suggestion-list { display: flex; flex-direction: column; }
  .pu-suggestion-row {
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    padding: 13px 20px; border-bottom: 1px solid ${C.border}; transition: background .12s;
  }
  .pu-suggestion-row:last-child { border-bottom: none; }
  .pu-suggestion-row:hover { background: rgba(255,255,255,0.03); }
  .pu-suggestion-name { font-size: 0.85rem; font-weight: 700; color: ${C.text}; margin-bottom: 4px; }
  .pu-suggestion-meta { display: flex; flex-wrap: wrap; gap: 8px; }
  .pu-suggestion-meta span { font-size: 0.72rem; color: ${C.muted}; }
  .pu-suggestion-side { display: flex; flex-direction: column; align-items: flex-end; gap: 8px; flex-shrink: 0; }
  .pu-suggestion-qty { font-size: 0.75rem; font-weight: 700; color: ${C.sub}; white-space: nowrap; }

  .pu-btn-outline {
    padding: 7px 14px; background: transparent; border: 1px solid ${C.border};
    border-radius: 8px; color: ${C.sub}; font-size: 0.78rem; font-weight: 600;
    cursor: pointer; transition: border-color .15s, color .15s; white-space: nowrap;
  }
  .pu-btn-outline:hover { border-color: ${C.gold}; color: ${C.gold}; }
  .pu-btn-outline:disabled { opacity: .45; cursor: not-allowed; }
  .pu-btn-sm { padding: 5px 11px; font-size: 0.75rem; }

  .pu-side {
    background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 14px;
    padding: 18px 16px; display: flex; flex-direction: column; gap: 6px;
  }
  .pu-side-title { font-size: 0.85rem; font-weight: 700; color: ${C.text}; }
  .pu-side-sub { font-size: 0.73rem; color: ${C.muted}; margin-bottom: 6px; }
  .pu-divider { border: none; border-top: 1px solid ${C.border}; margin: 10px 0; }

  .pu-queue-list { display: flex; flex-direction: column; gap: 4px; }
  .pu-queue-item {
    width: 100%; display: flex; justify-content: space-between; align-items: center;
    padding: 10px 12px; border-radius: 10px; background: rgba(255,255,255,0.03);
    border: 1px solid ${C.border}; cursor: pointer; transition: border-color .15s;
    text-align: left;
  }
  .pu-queue-item:hover { border-color: ${C.teal}; }
  .pu-order-code { font-size: 0.8rem; font-weight: 700; color: ${C.text}; }
  .pu-queue-meta { font-size: 0.7rem; color: ${C.muted}; margin-top: 2px; }
  .pu-queue-link { font-size: 0.72rem; font-weight: 700; color: ${C.teal}; white-space: nowrap; }
  .pu-queue-empty { font-size: 0.75rem; color: ${C.muted}; padding: 8px 0; }

  .pu-toolbar {
    display: flex; justify-content: space-between; align-items: flex-start;
    padding: 18px 20px 14px; border-bottom: 1px solid ${C.border}; flex-wrap: wrap; gap: 12px;
  }
  .pu-toolbar-title { font-size: 0.9rem; font-weight: 700; color: ${C.text}; margin-bottom: 10px; }
  .pu-filter-row { display: flex; flex-wrap: wrap; gap: 6px; }
  .pu-filter-chip {
    display: flex; align-items: center; gap: 6px; padding: 5px 12px;
    border-radius: 999px; background: transparent; border: 1px solid ${C.border};
    color: ${C.muted}; font-size: 0.73rem; font-weight: 600; cursor: pointer; transition: all .15s;
  }
  .pu-filter-chip span:last-child {
    padding: 1px 6px; border-radius: 999px; background: rgba(255,255,255,0.07);
    font-size: 0.65rem; font-weight: 700;
  }
  .pu-filter-chip:hover { border-color: ${C.gold}; color: ${C.gold}; }
  .pu-filter-chip.active { border-color: ${C.gold}; color: ${C.gold}; background: rgba(240,180,41,0.1); }
  .pu-toolbar-meta { font-size: 0.72rem; color: ${C.muted}; white-space: nowrap; padding-top: 4px; }

  .pu-table-scroll { overflow-x: auto; }
  .pu-table { width: 100%; border-collapse: collapse; }
  .pu-table th {
    padding: 10px 16px; background: rgba(255,255,255,0.03); border-bottom: 1px solid ${C.border};
    font-size: 0.7rem; font-weight: 700; color: ${C.muted}; text-transform: uppercase;
    letter-spacing: .06em; text-align: left; white-space: nowrap;
  }
  .pu-table td {
    padding: 13px 16px; border-bottom: 1px solid rgba(42,46,69,0.6);
    font-size: 0.83rem; vertical-align: middle;
  }
  .pu-table tbody tr:last-child td { border-bottom: none; }
  .pu-table tbody tr { transition: background .12s; }
  .pu-table tbody tr:hover { background: rgba(255,255,255,0.025); }
  .pu-cell-name { font-weight: 700; color: ${C.text}; }
  .pu-order-meta { font-size: 0.7rem; color: ${C.muted}; margin-top: 2px; }
  .pu-mono { font-family: 'JetBrains Mono', monospace; font-weight: 800; color: ${C.text}; }
  .pu-due-row { font-size: 0.68rem; font-family: 'JetBrains Mono', monospace; margin-top: 2px; }
  .pu-due-positive { color: ${C.coral}; }
  .pu-due-clear { color: ${C.teal}; }

  .pu-badge {
    display: inline-block; padding: 3px 9px; border-radius: 999px;
    font-size: 0.7rem; font-weight: 800; border: 1px solid; white-space: nowrap;
  }
  .pu-action-btns { display: flex; gap: 6px; flex-wrap: wrap; }
  .pu-btn-pay {
    padding: 5px 11px; background: transparent; border: 1px solid ${C.border};
    border-radius: 7px; color: ${C.sub}; font-size: 0.74rem; font-weight: 700;
    cursor: pointer; transition: all .15s;
  }
  .pu-btn-pay:hover { border-color: ${C.gold}; color: ${C.gold}; }
  .pu-btn-receive {
    padding: 5px 11px; background: ${C.teal}; border: none; border-radius: 7px;
    color: #0B0E17; font-size: 0.74rem; font-weight: 700; cursor: pointer; transition: opacity .15s;
  }
  .pu-btn-receive:hover { opacity: .85; }
  .pu-btn-order {
    padding: 5px 11px; background: transparent; border: 1px solid rgba(56,189,248,0.4);
    border-radius: 7px; color: ${C.sky}; font-size: 0.74rem; font-weight: 700;
    cursor: pointer; transition: all .15s;
  }
  .pu-btn-order:hover { background: rgba(56,189,248,0.1); }
  .pu-btn-order:disabled { opacity: .45; cursor: not-allowed; }
  .pu-btn-cancel {
    padding: 5px 11px; background: transparent; border: 1px solid rgba(255,107,107,0.35);
    border-radius: 7px; color: ${C.coral}; font-size: 0.74rem; font-weight: 700;
    cursor: pointer; transition: all .15s;
  }
  .pu-btn-cancel:hover { background: rgba(255,107,107,0.08); }
  .pu-btn-cancel:disabled { opacity: .45; cursor: not-allowed; }

  .pu-empty { display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 52px 24px; gap: 6px; text-align: center; }
  .pu-empty-icon { font-size: 2rem; margin-bottom: 6px; }
  .pu-empty-title { font-size: 0.95rem; font-weight: 700; color: ${C.text}; }
  .pu-empty-sub { font-size: 0.82rem; color: ${C.muted}; max-width: 320px; }
  .pu-empty-err { color: ${C.coral}; font-size: 0.85rem; margin-bottom: 8px; }

  /* Modals */
  .pu-overlay {
    position: fixed; inset: 0; background: rgba(7,9,14,0.75);
    backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center;
    padding: 20px; z-index: 1000;
  }
  .pu-modal {
    background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 16px;
    width: 100%; max-height: 90vh; overflow-y: auto; animation: pu-enter .18s ease;
  }
  @keyframes pu-enter { from { opacity: 0; transform: translateY(12px) scale(.97); } to { opacity: 1; transform: none; } }
  .pu-modal-head {
    display: flex; justify-content: space-between; align-items: flex-start;
    padding: 20px 22px 16px; border-bottom: 1px solid ${C.border};
  }
  .pu-modal-title { font-size: 1rem; font-weight: 800; color: ${C.text}; }
  .pu-modal-sub { font-size: 0.73rem; color: ${C.muted}; margin-top: 3px; }
  .pu-modal-close {
    padding: 6px 12px; background: transparent; border: 1px solid ${C.border};
    border-radius: 8px; color: ${C.muted}; font-size: 0.78rem; cursor: pointer;
    transition: all .15s; flex-shrink: 0;
  }
  .pu-modal-close:hover { border-color: ${C.coral}; color: ${C.coral}; }

  .pu-alert {
    margin: 0 22px 0; padding: 10px 14px; border-radius: 9px;
    background: rgba(255,107,107,0.1); border: 1px solid rgba(255,107,107,0.3);
    color: ${C.coral}; font-size: 0.8rem;
  }
  .pu-form-body { padding: 18px 22px; display: flex; flex-direction: column; gap: 14px; }
  .pu-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  @media (max-width: 540px) { .pu-form-row { grid-template-columns: 1fr; } }
  .pu-form-group { display: flex; flex-direction: column; gap: 6px; }
  .pu-label { font-size: 0.73rem; font-weight: 600; color: ${C.muted}; }
  .pu-input {
    padding: 9px 12px; background: rgba(255,255,255,0.04); border: 1px solid ${C.border};
    border-radius: 9px; color: ${C.text}; font-size: 0.83rem; outline: none;
    transition: border-color .15s; width: 100%; box-sizing: border-box;
    font-family: inherit;
  }
  .pu-input:focus { border-color: ${C.teal}; }
  .pu-input:disabled { opacity: .5; cursor: not-allowed; }
  select.pu-input option { background: ${C.surface}; }
  .pu-input-info {
    padding: 9px 12px; background: rgba(255,255,255,0.02); border: 1px solid ${C.border};
    border-radius: 9px; color: ${C.muted}; font-size: 0.75rem; line-height: 1.4;
  }
  .pu-unit-warn { font-size: 0.7rem; color: ${C.coral}; margin-top: 3px; }

  .pu-line-list { display: flex; flex-direction: column; gap: 10px; padding: 0 22px; }
  .pu-line-card {
    background: rgba(255,255,255,0.025); border: 1px solid ${C.border};
    border-radius: 12px; padding: 14px 16px; display: flex; flex-direction: column; gap: 12px;
  }
  .pu-line-footer {
    display: flex; justify-content: space-between; align-items: center;
    padding-top: 6px; border-top: 1px solid ${C.border};
  }
  .pu-line-total { font-size: 0.78rem; font-weight: 700; color: ${C.gold}; font-family: 'JetBrains Mono', monospace; }

  .pu-modal-add-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 14px 22px; border-top: 1px solid ${C.border};
  }
  .pu-modal-grand-total {
    font-size: 0.9rem; font-weight: 800; color: ${C.teal};
    font-family: 'JetBrains Mono', monospace;
  }
  .pu-modal-footer {
    display: flex; justify-content: flex-end; gap: 10px;
    padding: 14px 22px; border-top: 1px solid ${C.border};
  }

  .pu-receive-list { display: flex; flex-direction: column; gap: 10px; padding: 0 22px; }
  .pu-receive-card {
    background: rgba(255,255,255,0.025); border: 1px solid ${C.border};
    border-radius: 12px; padding: 14px 16px; display: flex; flex-direction: column; gap: 10px;
  }
  .pu-receive-head {
    display: flex; justify-content: space-between; align-items: baseline; gap: 10px;
  }
  .pu-receive-name { font-size: 0.85rem; font-weight: 700; color: ${C.text}; }
  .pu-receive-outstanding { font-size: 0.73rem; color: ${C.muted}; }
  .pu-receive-hint { font-size: 0.72rem; color: ${C.muted}; }

  .pu-pay-summary {
    margin: 0 22px; padding: 14px 16px; border-radius: 10px;
    background: rgba(255,255,255,0.03); border: 1px solid ${C.border};
    display: flex; flex-direction: column; gap: 8px;
  }
  .pu-pay-row { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; }
  .pu-pay-label { font-size: 0.77rem; color: ${C.muted}; }
  .pu-pay-val { font-size: 0.85rem; font-weight: 700; color: ${C.text}; font-family: 'JetBrains Mono', monospace; }
  .pu-pay-due { color: ${C.coral}; }
`;

const STATUS_CONFIG = {
  draft:              { label: 'Draft',     bg: 'rgba(136,146,164,0.1)',  color: '#8892A4', border: 'rgba(136,146,164,0.2)'  },
  ordered:            { label: 'Ordered',   bg: 'rgba(56,189,248,0.12)',  color: '#38BDF8', border: 'rgba(56,189,248,0.3)'   },
  partially_received: { label: 'Partial',   bg: 'rgba(240,180,41,0.12)',  color: '#F0B429', border: 'rgba(240,180,41,0.3)'   },
  received:           { label: 'Received',  bg: 'rgba(0,201,177,0.12)',   color: '#00C9B1', border: 'rgba(0,201,177,0.3)'    },
  cancelled:          { label: 'Cancelled', bg: 'rgba(255,107,107,0.12)', color: '#FF6B6B', border: 'rgba(255,107,107,0.3)'  },
};

const BILL_STATUS_CONFIG = {
  unpaid:  { label: 'Unpaid',    bg: 'rgba(255,107,107,0.12)', color: '#FF6B6B', border: 'rgba(255,107,107,0.3)' },
  partial: { label: 'Part paid', bg: 'rgba(240,180,41,0.12)',  color: '#F0B429', border: 'rgba(240,180,41,0.3)'  },
  paid:    { label: 'Paid',      bg: 'rgba(0,201,177,0.12)',   color: '#00C9B1', border: 'rgba(0,201,177,0.3)'   },
};

const EMPTY_LINE = { itemId: '', unitId: '', quantityOrdered: '', unitCost: '', expiryDate: '' };

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-GH', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function getPurchaseUnits(item) {
  return Array.isArray(item?.units) ? item.units.filter((unit) => unit.allowPurchase) : [];
}

function getPreferredPurchaseUnit(item) {
  const purchaseUnits = getPurchaseUnits(item);
  if (!purchaseUnits.length) return null;
  return [...purchaseUnits].sort((a, b) => {
    const conversionDiff = Number(b.conversionQty || 1) - Number(a.conversionQty || 1);
    if (conversionDiff !== 0) return conversionDiff;
    return Number(a.sortOrder || 0) - Number(b.sortOrder || 0);
  })[0];
}

function formatUnitCount(count, unitName = 'Unit') {
  const safeCount = Number(count || 0);
  return `${safeCount} ${unitName}${safeCount === 1 ? '' : 's'}`;
}

function formatUnitSummary(unit, baseUnitName) {
  if (!unit) return 'No purchase unit selected';
  const conversionQty = Number(unit.conversionQty || 1);
  return `1 ${String(unit.name).toLowerCase()} = ${conversionQty} ${String(baseUnitName || 'unit').toLowerCase()}${conversionQty === 1 ? '' : 's'}`;
}

export default function Purchases() {
  const [orders, setOrders] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [showReceive, setShowReceive] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [selectedPayOrder, setSelectedPayOrder] = useState(null);

  const [createForm, setCreateForm] = useState({
    supplierId: '', expectedDate: '', status: 'ordered', notes: '',
    items: [{ ...EMPTY_LINE }],
  });
  const [receiveForm, setReceiveForm] = useState({ items: [] });
  const [paymentForm, setPaymentForm] = useState({ amount: '', paymentMethod: 'cash', notes: '' });

  const [formError, setFormError] = useState('');
  const [receiveError, setReceiveError] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [saving, setSaving] = useState(false);
  const [receiveSaving, setReceiveSaving] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [rowBusy, setRowBusy] = useState({});
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => setToast({ message, type });
  const setBusy = (id, key, value) => setRowBusy((prev) => ({ ...prev, [`${id}-${key}`]: value }));
  const isBusy = (id, key) => Boolean(rowBusy[`${id}-${key}`]);

  const load = async () => {
    setPageLoading(true);
    setPageError('');
    try {
      const [ordersRes, suggestionsRes, suppliersRes, inventoryRes] = await Promise.all([
        api.get('/purchases'),
        api.get('/purchases/restock-suggestions'),
        api.get('/suppliers'),
        api.get('/inventory'),
      ]);
      setOrders(ordersRes.data);
      setSuggestions(suggestionsRes.data);
      setSuppliers(suppliersRes.data);
      setInventory(inventoryRes.data);
    } catch (error) {
      setPageError(error.response?.data?.error || 'Failed to load purchases. Please try again.');
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resetCreateForm = () => {
    setCreateForm({ supplierId: '', expectedDate: '', status: 'ordered', notes: '', items: [{ ...EMPTY_LINE }] });
    setFormError('');
  };

  const openNewOrder = (prefill = null) => {
    setCreateForm({
      supplierId: prefill?.supplierId ? String(prefill.supplierId) : '',
      expectedDate: '',
      status: 'ordered',
      notes: prefill?.notes || '',
      items: prefill?.items?.length
        ? prefill.items.map((item) => ({
            itemId: String(item.itemId),
            unitId: item.unitId != null ? String(item.unitId) : '',
            quantityOrdered: String(item.quantityOrdered),
            unitCost: String(item.unitCost),
            expiryDate: item.expiryDate || '',
          }))
        : [{ ...EMPTY_LINE }],
    });
    setFormError('');
    setShowCreate(true);
  };

  const addLine = () => {
    setCreateForm((prev) => ({ ...prev, items: [...prev.items, { ...EMPTY_LINE }] }));
  };

  const removeLine = (index) => {
    setCreateForm((prev) => ({ ...prev, items: prev.items.filter((_, idx) => idx !== index) }));
  };

  const updateLine = (index, field, value) => {
    setCreateForm((prev) => ({
      ...prev,
      items: prev.items.map((line, idx) => {
        if (idx !== index) return line;
        const nextLine = { ...line, [field]: value };

        if (field === 'itemId') {
          const item = inventory.find((candidate) => candidate.id === Number(value));
          if (item) {
            const preferredUnit = getPreferredPurchaseUnit(item);
            const conversionQty = Number(preferredUnit?.conversionQty || 1);
            nextLine.unitId = preferredUnit ? String(preferredUnit.id) : '';
            nextLine.unitCost = preferredUnit?.costPrice != null
              ? String(preferredUnit.costPrice)
              : (item.cost != null ? String(item.cost) : '');
            if (!nextLine.quantityOrdered) {
              nextLine.quantityOrdered = String(Math.max(1, Math.ceil(Number(item.reorderQuantity || 1) / conversionQty)));
            }
          } else {
            nextLine.unitId = '';
          }
        }

        if (field === 'unitId') {
          const item = inventory.find((candidate) => candidate.id === Number(nextLine.itemId));
          const selectedUnit = getPurchaseUnits(item).find((unit) => unit.id === Number(value));
          if (selectedUnit) {
            nextLine.unitCost = selectedUnit.costPrice != null ? String(selectedUnit.costPrice) : nextLine.unitCost;
          }
        }

        return nextLine;
      }),
    }));
  };

  const createTotal = useMemo(() => createForm.items.reduce((sum, line) => {
    const qty = Number(line.quantityOrdered) || 0;
    const cost = Number(line.unitCost) || 0;
    return sum + (qty * cost);
  }, 0), [createForm.items]);

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');

    const lines = createForm.items
      .map((line) => ({
        itemId: Number(line.itemId),
        unitId: line.unitId ? Number(line.unitId) : null,
        quantityOrdered: Number(line.quantityOrdered),
        unitCost: Number(line.unitCost),
        expiryDate: line.expiryDate || null,
      }))
      .filter((line) => line.itemId && line.quantityOrdered > 0 && line.unitCost >= 0);

    if (!createForm.supplierId) { setSaving(false); setFormError('Choose a supplier first.'); return; }
    if (!lines.length) { setSaving(false); setFormError('Add at least one product with quantity and cost.'); return; }

    try {
      await api.post('/purchases', {
        supplierId: Number(createForm.supplierId),
        expectedDate: createForm.expectedDate || null,
        status: createForm.status,
        notes: createForm.notes,
        items: lines,
      });
      setShowCreate(false);
      resetCreateForm();
      await load();
      showToast('Purchase order created.');
    } catch (error) {
      setFormError(error.response?.data?.error || 'Failed to create purchase order.');
    } finally {
      setSaving(false);
    }
  };

  const openReceive = (order) => {
    setSelectedOrder(order);
    setReceiveForm({
      items: order.items
        .filter((item) => item.quantityOutstanding > 0)
        .map((item) => ({
          id: item.id,
          name: item.item?.name || 'Product',
          unitName: item.unitName || item.item?.baseUnitName || 'Unit',
          baseUnitName: item.item?.baseUnitName || 'Unit',
          conversionQty: item.conversionQty || 1,
          quantityOutstanding: item.quantityOutstanding,
          baseQuantityOutstanding: item.baseQuantityOutstanding || item.quantityOutstanding,
          quantityReceived: item.quantityOutstanding,
          expiryDate: item.expiryDate ? new Date(item.expiryDate).toISOString().split('T')[0] : '',
        })),
    });
    setReceiveError('');
    setShowReceive(true);
  };

  const updateReceiveLine = (index, field, value) => {
    setReceiveForm((prev) => ({
      ...prev,
      items: prev.items.map((item, idx) => (idx === index ? { ...item, [field]: value } : item)),
    }));
  };

  const handleReceive = async (e) => {
    e.preventDefault();
    if (!selectedOrder) return;
    setReceiveSaving(true);
    setReceiveError('');

    const payloadItems = receiveForm.items
      .map((item) => ({ id: item.id, quantityReceived: Number(item.quantityReceived) || 0, expiryDate: item.expiryDate || null }))
      .filter((item) => item.quantityReceived > 0);

    if (!payloadItems.length) { setReceiveSaving(false); setReceiveError('Enter at least one received quantity.'); return; }

    try {
      await api.post(`/purchases/${selectedOrder.id}/receive`, { items: payloadItems });
      setShowReceive(false);
      setSelectedOrder(null);
      await load();
      showToast('Stock received successfully.');
    } catch (error) {
      setReceiveError(error.response?.data?.error || 'Failed to receive stock.');
    } finally {
      setReceiveSaving(false);
    }
  };

  const openPay = (order) => {
    setSelectedPayOrder(order);
    setPaymentForm({ amount: String(Number(order.balanceDue || 0).toFixed(2)), paymentMethod: 'cash', notes: '' });
    setPaymentError('');
    setShowPay(true);
  };

  const closePay = (force = false) => {
    if (paymentSaving && !force) return;
    setShowPay(false);
    setSelectedPayOrder(null);
    setPaymentForm({ amount: '', paymentMethod: 'cash', notes: '' });
    setPaymentError('');
  };

  const handlePay = async (e) => {
    e.preventDefault();
    if (!selectedPayOrder) return;
    setPaymentSaving(true);
    setPaymentError('');
    try {
      await api.post(`/purchases/${selectedPayOrder.id}/pay`, {
        amount: Number(paymentForm.amount),
        paymentMethod: paymentForm.paymentMethod,
        notes: paymentForm.notes,
      });
      closePay(true);
      await load();
      showToast('Supplier payment recorded.');
    } catch (error) {
      setPaymentError(error.response?.data?.error || 'Failed to record supplier payment.');
    } finally {
      setPaymentSaving(false);
    }
  };

  const updateOrderStatus = async (order, status) => {
    setBusy(order.id, status, true);
    try {
      await api.put(`/purchases/${order.id}`, { status });
      await load();
      showToast(status === 'ordered' ? 'Purchase order marked as ordered.' : 'Purchase order updated.');
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to update purchase order.', 'error');
    } finally {
      setBusy(order.id, status, false);
    }
  };

  const awaitingReceive = orders.filter((o) => ['ordered', 'partially_received'].includes(o.status));
  const totalCommitted = orders.filter((o) => o.status !== 'cancelled').reduce((s, o) => s + Number(o.total || 0), 0);
  const totalOutstandingBills = orders.filter((o) => o.status !== 'cancelled').reduce((s, o) => s + Number(o.balanceDue || 0), 0);
  const startOfMonth = new Date(); startOfMonth.setDate(1); startOfMonth.setHours(0, 0, 0, 0);
  const totalPaidThisMonth = orders.reduce((s, o) => s + (Array.isArray(o.payments)
    ? o.payments.reduce((inner, p) => (new Date(p.createdAt) >= startOfMonth ? inner + Number(p.amount || 0) : inner), 0)
    : 0), 0);
  const totalOutstandingBaseUnits = awaitingReceive.reduce((s, o) => s + Number(o.outstandingBaseUnits || 0), 0);
  const activeSupplierCount = new Set(orders.map((o) => o.supplierId)).size;
  const supplierBills = orders
    .filter((o) => o.status !== 'cancelled' && Number(o.balanceDue || 0) > 0)
    .sort((a, b) => Number(b.balanceDue || 0) - Number(a.balanceDue || 0));
  const displayOrders = statusFilter ? orders.filter((o) => o.status === statusFilter) : orders;

  return (
    <Layout>
      <style>{STYLES}</style>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="pu-header">
        <div>
          <div className="pu-title-row">
            <h2>Purchases</h2>
            <span className="pu-count">{orders.length}</span>
          </div>
          <div className="pu-subtitle">Order stock, receive it cleanly, and tie it back to suppliers.</div>
        </div>
        <button className="pu-btn-primary" onClick={() => openNewOrder()}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New PO
        </button>
      </div>

      <div className="pu-body">
        <div className="pu-kpi-grid">
          <article className="pu-kpi pu-kpi--featured">
            <div className="pu-kpi-label">Committed Spend</div>
            <div className="pu-kpi-value">{fmt(totalCommitted)}</div>
            <div className="pu-kpi-sub">{awaitingReceive.length} orders awaiting receipt</div>
            <div className="pu-kpi-mini">
              <span>{totalOutstandingBaseUnits} base units outstanding</span>
              <span>{activeSupplierCount} active suppliers</span>
              <span>{suggestions.length} restock suggestions</span>
            </div>
          </article>

          <article className="pu-kpi">
            <div className="pu-kpi-icon pu-kpi-icon--coral">PO</div>
            <div className="pu-kpi-label">Outstanding Bills</div>
            <div className="pu-kpi-num">{fmt(totalOutstandingBills)}</div>
            <div className="pu-kpi-caption">{supplierBills.length} unpaid or part-paid orders</div>
          </article>

          <article className="pu-kpi">
            <div className="pu-kpi-icon pu-kpi-icon--sky">S</div>
            <div className="pu-kpi-label">Paid This Month</div>
            <div className="pu-kpi-num">{fmt(totalPaidThisMonth)}</div>
            <div className="pu-kpi-caption">Supplier settlements recorded</div>
          </article>

          <article className="pu-kpi">
            <div className="pu-kpi-icon pu-kpi-icon--gold">!</div>
            <div className="pu-kpi-label">Awaiting Receive</div>
            <div className="pu-kpi-num">{awaitingReceive.length}</div>
            <div className="pu-kpi-caption">{totalOutstandingBaseUnits} base units outstanding</div>
          </article>

          <article className="pu-kpi">
            <div className="pu-kpi-icon pu-kpi-icon--teal">V</div>
            <div className="pu-kpi-label">Suppliers Active</div>
            <div className="pu-kpi-num">{activeSupplierCount}</div>
            <div className="pu-kpi-caption">{suggestions.length} restock suggestions live</div>
          </article>
        </div>

        <div className="pu-board">
          <div className="pu-section">
            <div className="pu-section-head">
              <div>
                <div className="pu-section-title">Restock Suggestions</div>
                <div className="pu-section-sub">Use live stock signals to start the next purchase order.</div>
              </div>
              <div className="pu-section-meta">{suggestions.length} low stock items</div>
            </div>

            {pageLoading ? (
              <SkeletonTable rows={4} cols={5} />
            ) : pageError ? (
              <div className="pu-empty">
                <p className="pu-empty-err">{pageError}</p>
                <button className="pu-btn-outline" onClick={load}>Retry</button>
              </div>
            ) : suggestions.length === 0 ? (
              <div className="pu-empty">
                <div className="pu-empty-icon">📦</div>
                <p className="pu-empty-title">Nothing urgent to reorder</p>
                <p className="pu-empty-sub">Low-stock products will show here when it is time to buy.</p>
              </div>
            ) : (
              <div className="pu-suggestion-list">
                {suggestions.slice(0, 8).map((item) => (
                  <div key={item.id} className="pu-suggestion-row">
                    <div>
                      <div className="pu-suggestion-name">{item.name}</div>
                      <div className="pu-suggestion-meta">
                        <span>{item.quantity} left</span>
                        <span>Reorder at {item.reorderLevel}</span>
                        <span>{item.preferredPurchaseUnitName ? `Buy in ${item.preferredPurchaseUnitName}` : 'No purchase unit enabled'}</span>
                        <span>{item.supplierName || 'No supplier linked'}</span>
                      </div>
                    </div>
                    <div className="pu-suggestion-side">
                      <div className="pu-suggestion-qty">
                        {item.preferredPurchaseUnitName
                          ? `Buy ${formatUnitCount(item.suggestedOrderQty, item.preferredPurchaseUnitName)}`
                          : 'Set purchase unit'}
                      </div>
                      <button
                        className="pu-btn-outline pu-btn-sm"
                        disabled={!item.supplierId || !item.preferredPurchaseUnitId}
                        onClick={() => openNewOrder({
                          supplierId: item.supplierId,
                          items: [{
                            itemId: item.id,
                            unitId: item.preferredPurchaseUnitId,
                            quantityOrdered: item.suggestedOrderQty || 1,
                            unitCost: item.suggestedUnitCost ?? (item.cost || 0),
                            expiryDate: '',
                          }],
                        })}
                      >
                        {!item.supplierId ? 'Add supplier first' : !item.preferredPurchaseUnitId ? 'Enable purchase unit' : 'Create PO'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <aside className="pu-side">
            <div className="pu-side-title">Receiving Queue</div>
            <div className="pu-side-sub">Orders waiting to become stock on hand.</div>
            <div className="pu-queue-list">
              {awaitingReceive.slice(0, 5).map((order) => (
                <button key={order.id} type="button" className="pu-queue-item" onClick={() => openReceive(order)}>
                  <div>
                    <div className="pu-order-code">{order.code}</div>
                    <div className="pu-queue-meta">{order.supplier?.name || 'Supplier'} • {order.outstandingBaseUnits} base units left</div>
                  </div>
                  <span className="pu-queue-link">Receive</span>
                </button>
              ))}
              {awaitingReceive.length === 0 && <div className="pu-queue-empty">No stock waiting to receive.</div>}
            </div>

            <hr className="pu-divider" />

            <div className="pu-side-title">Supplier Bills</div>
            <div className="pu-side-sub">Record partial or full supplier payments as they happen.</div>
            <div className="pu-queue-list">
              {supplierBills.slice(0, 5).map((order) => (
                <button key={`bill-${order.id}`} type="button" className="pu-queue-item" onClick={() => openPay(order)}>
                  <div>
                    <div className="pu-order-code">{order.code}</div>
                    <div className="pu-queue-meta">{order.supplier?.name || 'Supplier'} · Due {fmt(order.balanceDue)}</div>
                  </div>
                  <span className="pu-queue-link">Pay</span>
                </button>
              ))}
              {supplierBills.length === 0 && <div className="pu-queue-empty">No unpaid supplier bills right now.</div>}
            </div>
          </aside>
        </div>

        <div className="pu-section">
          <div className="pu-toolbar">
            <div>
              <div className="pu-toolbar-title">Purchase Orders</div>
              <div className="pu-filter-row">
                <button type="button" className={`pu-filter-chip${statusFilter === '' ? ' active' : ''}`} onClick={() => setStatusFilter('')}>
                  <span>All</span><span>{orders.length}</span>
                </button>
                {['draft', 'ordered', 'partially_received', 'received', 'cancelled'].map((status) => (
                  <button
                    key={status}
                    type="button"
                    className={`pu-filter-chip${statusFilter === status ? ' active' : ''}`}
                    onClick={() => setStatusFilter(status)}
                  >
                    <span>{STATUS_CONFIG[status].label}</span>
                    <span>{orders.filter((o) => o.status === status).length}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="pu-toolbar-meta">{displayOrders.length} orders</div>
          </div>

          {pageLoading ? (
            <SkeletonTable rows={6} cols={7} />
          ) : pageError ? (
            <div className="pu-empty">
              <p className="pu-empty-err">{pageError}</p>
              <button className="pu-btn-outline" onClick={load}>Retry</button>
            </div>
          ) : displayOrders.length === 0 ? (
            <div className="pu-empty">
              <div className="pu-empty-icon">{orders.length === 0 ? '🧾' : '🔎'}</div>
              <p className="pu-empty-title">{orders.length === 0 ? 'No purchase orders yet' : 'No orders in this view'}</p>
              <p className="pu-empty-sub">
                {orders.length === 0
                  ? 'Start your first supplier order and receive stock into inventory from here.'
                  : 'Try another status filter or create a fresh order.'}
              </p>
              <button className="pu-btn-primary" style={{ marginTop: 8 }} onClick={() => openNewOrder()}>
                {orders.length === 0 ? 'Create purchase order' : 'New purchase order'}
              </button>
            </div>
          ) : (
            <div className="pu-table-scroll">
              <table className="pu-table">
                <thead>
                  <tr>
                    <th>PO</th>
                    <th>Supplier</th>
                    <th>Items</th>
                    <th>Status</th>
                    <th>Expected</th>
                    <th>Total</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayOrders.map((order) => {
                    const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.ordered;
                    const bill = BILL_STATUS_CONFIG[order.billStatus] || BILL_STATUS_CONFIG.unpaid;
                    return (
                      <tr key={order.id}>
                        <td>
                          <div className="pu-order-code">{order.code}</div>
                          <div className="pu-order-meta">{formatDate(order.orderedAt || order.createdAt)}</div>
                        </td>
                        <td>
                          <div className="pu-cell-name">{order.supplier?.name || 'Supplier'}</div>
                          <div className="pu-order-meta">{order.supplier?.phone || order.supplier?.email || 'No contact saved'}</div>
                        </td>
                        <td>
                          <div className="pu-cell-name">{order.itemCount} item{order.itemCount !== 1 ? 's' : ''}</div>
                          <div className="pu-order-meta">{order.outstandingBaseUnits} base units outstanding</div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'flex-start' }}>
                            <span className="pu-badge" style={{ background: status.bg, color: status.color, borderColor: status.border }}>
                              {status.label}
                            </span>
                            <span className="pu-badge" style={{ background: bill.bg, color: bill.color, borderColor: bill.border }}>
                              {bill.label}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="pu-cell-name">{formatDate(order.expectedDate)}</div>
                          <div className="pu-order-meta">
                            {order.receivedAt ? `Closed ${formatDate(order.receivedAt)}` : 'Open'}
                          </div>
                        </td>
                        <td>
                          <div className="pu-mono">{fmt(order.total)}</div>
                          <div className="pu-due-row" style={{ color: C.muted }}>Paid {fmt(order.amountPaid || 0)}</div>
                          <div className={`pu-due-row ${Number(order.balanceDue || 0) > 0 ? 'pu-due-positive' : 'pu-due-clear'}`}>
                            Due {fmt(order.balanceDue || 0)}
                          </div>
                        </td>
                        <td>
                          <div className="pu-action-btns">
                            {Number(order.balanceDue || 0) > 0 && order.status !== 'cancelled' && (
                              <button className="pu-btn-pay" onClick={() => openPay(order)}>Pay</button>
                            )}
                            {order.status === 'draft' && (
                              <button
                                className="pu-btn-order"
                                disabled={isBusy(order.id, 'ordered')}
                                onClick={() => updateOrderStatus(order, 'ordered')}
                              >
                                {isBusy(order.id, 'ordered') ? 'Saving...' : 'Order'}
                              </button>
                            )}
                            {['ordered', 'partially_received'].includes(order.status) && (
                              <button className="pu-btn-receive" onClick={() => openReceive(order)}>Receive</button>
                            )}
                            {!['received', 'cancelled'].includes(order.status) && (
                              <button
                                className="pu-btn-cancel"
                                disabled={isBusy(order.id, 'cancelled')}
                                onClick={() => updateOrderStatus(order, 'cancelled')}
                              >
                                {isBusy(order.id, 'cancelled') ? 'Saving...' : 'Cancel'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <div className="pu-overlay" onClick={(e) => e.target === e.currentTarget && setShowCreate(false)}>
          <div className="pu-modal" style={{ maxWidth: 780 }}>
            <div className="pu-modal-head">
              <div className="pu-modal-title">Create Purchase Order</div>
              <button className="pu-modal-close" onClick={() => setShowCreate(false)}>✕</button>
            </div>
            {formError && <div className="pu-alert" style={{ margin: '14px 22px 0' }}>{formError}</div>}

            <form onSubmit={handleCreateOrder}>
              <div className="pu-form-body">
                <div className="pu-form-row">
                  <div className="pu-form-group">
                    <label className="pu-label">Supplier</label>
                    <select className="pu-input" value={createForm.supplierId} onChange={(e) => setCreateForm((prev) => ({ ...prev, supplierId: e.target.value }))}>
                      <option value="">Select supplier</option>
                      {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div className="pu-form-group">
                    <label className="pu-label">Expected date</label>
                    <input className="pu-input" type="date" value={createForm.expectedDate} onChange={(e) => setCreateForm((prev) => ({ ...prev, expectedDate: e.target.value }))} />
                  </div>
                </div>
                <div className="pu-form-row">
                  <div className="pu-form-group">
                    <label className="pu-label">Status</label>
                    <select className="pu-input" value={createForm.status} onChange={(e) => setCreateForm((prev) => ({ ...prev, status: e.target.value }))}>
                      <option value="ordered">Ordered</option>
                      <option value="draft">Draft</option>
                    </select>
                  </div>
                  <div className="pu-form-group">
                    <label className="pu-label">Notes</label>
                    <input className="pu-input" value={createForm.notes} onChange={(e) => setCreateForm((prev) => ({ ...prev, notes: e.target.value }))} placeholder="Optional supplier or delivery note" />
                  </div>
                </div>
              </div>

              <div className="pu-line-list">
                {createForm.items.map((line, index) => {
                  const inventoryItem = inventory.find((item) => item.id === Number(line.itemId));
                  const purchaseUnits = getPurchaseUnits(inventoryItem);
                  const selectedUnit = purchaseUnits.find((unit) => unit.id === Number(line.unitId)) || getPreferredPurchaseUnit(inventoryItem);
                  const baseUnitName = inventoryItem?.baseUnitName || inventoryItem?.baseUnit?.name || 'Unit';
                  const conversionQty = Number(selectedUnit?.conversionQty || 1);
                  const baseQtyPreview = (Number(line.quantityOrdered) || 0) * conversionQty;

                  return (
                    <div key={`line-${index}`} className="pu-line-card">
                      <div className="pu-form-row">
                        <div className="pu-form-group">
                          <label className="pu-label">Product</label>
                          <select className="pu-input" value={line.itemId} onChange={(e) => updateLine(index, 'itemId', e.target.value)}>
                            <option value="">Select product</option>
                            {inventory.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                          </select>
                        </div>
                        <div className="pu-form-group">
                          <label className="pu-label">Purchase unit</label>
                          <select
                            className="pu-input"
                            value={line.unitId}
                            onChange={(e) => updateLine(index, 'unitId', e.target.value)}
                            disabled={!inventoryItem || purchaseUnits.length === 0}
                          >
                            <option value="">{inventoryItem ? 'Select unit' : 'Choose product first'}</option>
                            {purchaseUnits.map((unit) => (
                              <option key={unit.id} value={unit.id}>
                                {unit.name} ({unit.conversionQty} {String(baseUnitName).toLowerCase()}{Number(unit.conversionQty) === 1 ? '' : 's'})
                              </option>
                            ))}
                          </select>
                          {inventoryItem && purchaseUnits.length === 0 && (
                            <div className="pu-unit-warn">Enable at least one purchase unit for this item in Inventory.</div>
                          )}
                        </div>
                      </div>
                      <div className="pu-form-row">
                        <div className="pu-form-group">
                          <label className="pu-label">Qty ordered</label>
                          <input className="pu-input" type="number" min="1" value={line.quantityOrdered} onChange={(e) => updateLine(index, 'quantityOrdered', e.target.value)} />
                        </div>
                        <div className="pu-form-group">
                          <label className="pu-label">Unit cost</label>
                          <input className="pu-input" type="number" min="0" step="0.01" value={line.unitCost} onChange={(e) => updateLine(index, 'unitCost', e.target.value)} />
                        </div>
                      </div>
                      <div className="pu-form-row">
                        <div className="pu-form-group">
                          <label className="pu-label">Expiry date</label>
                          <input className="pu-input" type="date" value={line.expiryDate} onChange={(e) => updateLine(index, 'expiryDate', e.target.value)} />
                        </div>
                        <div className="pu-form-group">
                          <label className="pu-label">Base stock impact</label>
                          <div className="pu-input-info">
                            {selectedUnit
                              ? `${formatUnitSummary(selectedUnit, baseUnitName)} • This line adds ${baseQtyPreview} ${String(baseUnitName).toLowerCase()}${baseQtyPreview === 1 ? '' : 's'}`
                              : 'Choose a purchase unit to see the stock conversion'}
                          </div>
                        </div>
                      </div>
                      <div className="pu-line-footer">
                        <span className="pu-line-total">Line total {fmt((Number(line.quantityOrdered) || 0) * (Number(line.unitCost) || 0))}</span>
                        {createForm.items.length > 1 && (
                          <button type="button" className="pu-btn-outline pu-btn-sm" onClick={() => removeLine(index)}>Remove</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="pu-modal-add-row">
                <button type="button" className="pu-btn-outline" onClick={addLine}>Add line</button>
                <div className="pu-modal-grand-total">Total {fmt(createTotal)}</div>
              </div>

              <div className="pu-modal-footer">
                <button type="button" className="pu-btn-outline" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="pu-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save purchase order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showReceive && selectedOrder && (
        <div className="pu-overlay" onClick={(e) => e.target === e.currentTarget && setShowReceive(false)}>
          <div className="pu-modal" style={{ maxWidth: 720 }}>
            <div className="pu-modal-head">
              <div>
                <div className="pu-modal-title">Receive Stock</div>
                <div className="pu-modal-sub">{selectedOrder.code} • {selectedOrder.supplier?.name || 'Supplier'}</div>
              </div>
              <button className="pu-modal-close" onClick={() => setShowReceive(false)}>✕</button>
            </div>
            {receiveError && <div className="pu-alert" style={{ margin: '14px 22px 0' }}>{receiveError}</div>}

            <form onSubmit={handleReceive}>
              <div className="pu-receive-list" style={{ padding: '18px 22px' }}>
                {receiveForm.items.map((line, index) => {
                  const receivingBaseQty = (Number(line.quantityReceived) || 0) * Number(line.conversionQty || 1);
                  return (
                    <div key={line.id} className="pu-receive-card">
                      <div className="pu-receive-head">
                        <span className="pu-receive-name">{line.name}</span>
                        <span className="pu-receive-outstanding">{formatUnitCount(line.quantityOutstanding, line.unitName)} outstanding</span>
                      </div>
                      <div className="pu-receive-hint">
                        {line.baseQuantityOutstanding} {String(line.baseUnitName).toLowerCase()}{Number(line.baseQuantityOutstanding) === 1 ? '' : 's'} still to receive
                      </div>
                      <div className="pu-form-row">
                        <div className="pu-form-group">
                          <label className="pu-label">Qty received ({line.unitName})</label>
                          <input
                            className="pu-input"
                            type="number" min="0" max={line.quantityOutstanding}
                            value={line.quantityReceived}
                            onChange={(e) => updateReceiveLine(index, 'quantityReceived', e.target.value)}
                          />
                        </div>
                        <div className="pu-form-group">
                          <label className="pu-label">Expiry date</label>
                          <input
                            className="pu-input"
                            type="date" value={line.expiryDate}
                            onChange={(e) => updateReceiveLine(index, 'expiryDate', e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="pu-receive-hint">
                        Receiving {formatUnitCount(line.quantityReceived || 0, line.unitName)} adds {receivingBaseQty} {String(line.baseUnitName).toLowerCase()}{receivingBaseQty === 1 ? '' : 's'} to stock.
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="pu-modal-footer">
                <button type="button" className="pu-btn-outline" onClick={() => setShowReceive(false)}>Cancel</button>
                <button type="submit" className="pu-btn-primary" disabled={receiveSaving}>
                  {receiveSaving ? 'Receiving...' : 'Post stock receipt'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPay && selectedPayOrder && (
        <div className="pu-overlay" onClick={(e) => e.target === e.currentTarget && closePay()}>
          <div className="pu-modal" style={{ maxWidth: 520 }}>
            <div className="pu-modal-head">
              <div>
                <div className="pu-modal-title">Record Supplier Payment</div>
                <div className="pu-modal-sub">{selectedPayOrder.code} · {selectedPayOrder.supplier?.name || 'Supplier'}</div>
              </div>
              <button className="pu-modal-close" onClick={() => closePay()}>✕</button>
            </div>
            {paymentError && <div className="pu-alert" style={{ margin: '14px 22px 0' }}>{paymentError}</div>}

            <div className="pu-pay-summary" style={{ margin: '16px 22px 0' }}>
              <div className="pu-pay-row">
                <span className="pu-pay-label">Order total</span>
                <span className="pu-pay-val">{fmt(selectedPayOrder.total)}</span>
              </div>
              <div className="pu-pay-row">
                <span className="pu-pay-label">Already paid</span>
                <span className="pu-pay-val">{fmt(selectedPayOrder.amountPaid || 0)}</span>
              </div>
              <div className="pu-pay-row">
                <span className="pu-pay-label">Still due</span>
                <span className="pu-pay-val pu-pay-due">{fmt(selectedPayOrder.balanceDue || 0)}</span>
              </div>
            </div>

            <form onSubmit={handlePay}>
              <div className="pu-form-body">
                <div className="pu-form-row">
                  <div className="pu-form-group">
                    <label className="pu-label">Amount</label>
                    <input
                      className="pu-input" type="number" min="0.01" step="0.01"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm((prev) => ({ ...prev, amount: e.target.value }))}
                    />
                  </div>
                  <div className="pu-form-group">
                    <label className="pu-label">Method</label>
                    <select className="pu-input" value={paymentForm.paymentMethod} onChange={(e) => setPaymentForm((prev) => ({ ...prev, paymentMethod: e.target.value }))}>
                      <option value="cash">Cash</option>
                      <option value="momo">MoMo</option>
                      <option value="card">Card</option>
                      <option value="bank">Bank transfer</option>
                    </select>
                  </div>
                </div>
                <div className="pu-form-group">
                  <label className="pu-label">Notes</label>
                  <textarea
                    className="pu-input" rows={2}
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm((prev) => ({ ...prev, notes: e.target.value }))}
                    style={{ resize: 'vertical' }}
                    placeholder="Optional payment note or reference"
                  />
                </div>
              </div>

              <div className="pu-modal-footer">
                <button type="button" className="pu-btn-outline" onClick={() => closePay()}>Cancel</button>
                <button type="submit" className="pu-btn-primary" disabled={paymentSaving}>
                  {paymentSaving ? 'Saving...' : 'Record payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
